import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaFolder,
  FaMoneyBillWave,
  FaTasks,
  FaSignOutAlt,
  FaUserCircle,
  FaChevronDown,
  FaChevronUp,
  FaGavel,
  FaFileAlt,
  FaTruck,
  FaFile,
  FaFileContract,
} from "react-icons/fa";
import { FiBox } from "react-icons/fi";
import { MdDashboard } from "react-icons/md";
import logo from "../../assets/logo.jpeg";

const OASidebar = ({ visible, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [collapsedMenus, setCollapsedMenus] = useState({});
  const isProcessingClick = useRef(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser({
          firstName: parsed.firstName || parsed.name?.split(" ")[0] || "User",
          lastName: parsed.lastName || parsed.name?.split(" ")[1] || "",
          email: parsed.email,
          profilePic: parsed.profilePic,
        });
      } catch (err) {
        console.error("Error parsing user from localStorage", err);
      }
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const toggleMenu = (label) => {
    // Prevent multiple clicks
    if (isProcessingClick.current) {
      return;
    }

    isProcessingClick.current = true;

    setCollapsedMenus((prev) => {
      const currentState = prev[label] === true;
      const newState = {
        ...prev,
        [label]: !currentState,
      };
      console.log("Toggling menu:", label, "from", currentState, "to", !currentState);
      return newState;
    });

    // Reset the flag after a short delay
    setTimeout(() => {
      isProcessingClick.current = false;
    }, 100);
  };

  // Close parent menu when a child is selected.
  const handleChildClick = (parentLabel) => {
    setCollapsedMenus((prev) => ({
      ...prev,
      [parentLabel]: false,
    }));
    // If mobile sidebar close handler provided, close the sidebar on navigation.
    if (typeof onClose === "function") {
      onClose();
    }
  };

  // MENU ITEMS (unchanged)
  const menuItems = [
    { label: "Dashboard", icon: <MdDashboard />, path: "/operations-dashboard" },
    {
      label: "Procurement",
      icon: <FaTasks />,
      children: [
        {
          label: "Procurement Planning",
          icon: <FaFolder />,
          path: "/project-procurement-planning",
        },
        {
          label: "Requests & Orders",
          icon: <FaTasks />,
          path: "/oa/procurement",
        },
      ],
    },
    {
      label: "Requisition Management",
      icon: <FaMoneyBillWave />,
      children: [
        // { label: "Travel", icon: <FaTruck />, path: "/travel" },
        { label: "Requisitions", icon: <FaFileAlt />, path: "/requisitions" },
      ],
    },
    {
      label: "Vendor Management",
      icon: <FaFileContract size={16} />,
      children: [
        { label: "Vendor Profiles", icon: <FaUserCircle size={14} />, path: "/vendor-management" },
        { label: "Vendor Contracts", icon: <FaFileContract size={14} />, path: "/vendor-contracts" },
        { label: "Vendor Invoices", icon: <FaFileContract size={14} />, path: "/vendor-invoices" },
      ],
    },
    { label: "Assets Management", icon: <FiBox />, path: "/assets-management" },
    { label: "Inventory Management", icon: <FaFolder />, path: "/inventory-management" },
    { label: "Reports", icon: <FaFolder />, path: "/oa/reports" },
    { label: "Disciplinary", icon: <FaGavel />, path: "/operations-discipline" },
    { label: "Timesheet", icon: <FaFileAlt />, path: "/oa/timesheet" },
    { label: "Documentation", icon: <FaFile />, path: "/oa/documentation" },
    { label: "My Profile", icon: <FaUserCircle />, path: "/associate-profile" },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-center py-6 flex-shrink-0">
        <img src={logo} alt="Logo" className="w-28" />
      </div>

      {/* Scrollable Menu with hidden scrollbar */}
      <div
        className="flex-1 overflow-y-auto px-4"
        style={{
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE/Edge
        }}
      >
        <ul className="space-y-1">
          {menuItems.map((item, index) => {
            const hasChildren = item.children && item.children.length > 0;
            const isActive = item.path && location.pathname === item.path;
            const isChildActive =
              hasChildren && item.children.some((child) => location.pathname === child.path);

            // Determine if menu should be open:
            // - If user explicitly toggled (true/false) use that.
            // - Otherwise default to open when a child route is active.
            const isOpen = (() => {
              if (!hasChildren) return false;
              const explicit = collapsedMenus[item.label];
              if (typeof explicit === "boolean") return explicit;
              return isChildActive;
            })();

            return (
              <li key={index}>
                <div className="flex flex-col">
                  {/* Parent Link */}
                  <div
                    className={`flex items-center justify-between px-3 py-2 rounded-lg
                      ${
                        isActive || isChildActive
                          ? "bg-orange-100 text-orange-600 font-semibold"
                          : "text-gray-700 hover:bg-gray-100 hover:text-orange-500 transition-all duration-200"
                      }
                    `}
                  >
                    {item.path ? (
                      <Link to={item.path} className="flex items-center gap-3 flex-1">
                        {item.icon}
                        <span>{item.label}</span>
                      </Link>
                    ) : (
                      <div className="flex items-center gap-3 flex-1">
                        {item.icon}
                        <span>{item.label}</span>
                      </div>
                    )}

                    {hasChildren && (
                      <div
                        onClick={() => toggleMenu(item.label)}
                        className="p-1 rounded hover:bg-gray-200 transition-colors cursor-pointer"
                        style={{ userSelect: "none" }}
                      >
                        {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                    )}
                  </div>

                  {/* Children */}
                  {hasChildren && isOpen && (
                    <ul className="ml-6 mt-1 space-y-1">
                      {item.children.map((child, cIndex) => {
                        const childIsActive = child.path && location.pathname === child.path;
                        return (
                          <li key={cIndex}>
                            <Link to={child.path} onClick={() => handleChildClick(item.label)}>
                              <div
                                className={`flex items-center gap-2 px-3 py-1 rounded-lg cursor-pointer
                                  ${
                                    childIsActive
                                      ? "bg-orange-50 text-orange-600 font-medium"
                                      : "text-gray-600 hover:bg-gray-100 hover:text-orange-500"
                                  }
                                `}
                              >
                                {child.icon}
                                <span>{child.label}</span>
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* User info + logout */}
      <div className="px-4 py-4 border-t flex-shrink-0">
        {user ? (
          <div className="flex items-center gap-3 mb-4">
            <div>
              <p className="text-sm font-semibold">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-xs text-gray-500">{user.email}</p>
            </div>
          </div>
        ) : (
          <div className="animate-pulse flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            <div>
              <div className="w-24 h-4 bg-gray-200 rounded mb-1"></div>
              <div className="w-32 h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        )}

        <button
          onClick={handleLogout}
          className="flex items-center w-full gap-3 px-3 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-all duration-200"
        >
          <FaSignOutAlt />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden md:flex flex-col fixed left-0 top-0 w-64 h-screen bg-white shadow-md z-50">
        <SidebarContent />
      </div>

      {/* Mobile Sidebar */}
      {visible && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
          <div className="relative bg-white w-64 h-full shadow-md z-50">
            <SidebarContent />
          </div>
        </div>
      )}
    </>
  );
};

export default OASidebar;
